<template>
  <LayoutColumns :loading="loading">
    <template #leftSidebar>
      <SidebarMenu
        :top-list="sorts"
        :bottom-list="categories"
        :is-active-item-hidden="isActiveCategoryHidden"
      />
    </template>
    <LayoutWithRightSidebar breakpoint="xl">
      <template #rightSidebar>
        <div class="flex flex-col gap-8">
          <div v-if="eventsStore.activeEvents?.length" class="max-xl:hidden">
            <div class="text-lg text-gray-900 font-bold pb-2">
              {{ $t('events.active_events') }}
            </div>
            <div class="flex flex-col gap-4">
              <div
                v-for="event in eventsStore.activeEvents"
                :key="event.id"
                class="rounded-xl bg-white p-4"
              >
                <EventsCard :event="event" />
              </div>
            </div>
          </div>
          <NewsRightSidebar />
        </div>
      </template>
      <div>
        <div v-if="eventsStore.activeEvents?.length" class="xl:hidden mb-6">
          <div class="text-2xl text-gray-900 font-bold pb-4 px-4">
            {{ $t('events.active_events') }}
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              v-for="event in eventsStore.activeEvents"
              :key="event.id"
              class="rounded-xl bg-white p-4"
            >
              <EventsCard :event="event" />
            </div>
          </div>
        </div>
        <div
          class="text-2xl text-gray-900 font-bold pb-4 px-4 hidden lg:block xl:hidden"
        >
          {{ $t('menu.feed') }}
        </div>
        <TopbarMenu :menu-list="topbarMenu" />
        <NuxtPage />
      </div>
    </LayoutWithRightSidebar>
  </LayoutColumns>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { ComputedRef } from 'vue'
import { useNavigation } from '~/stores/navigation'
import type { TopbarMenuInterface } from '~/models/menu'
import { NavigationSortType } from '~/models/navigation'
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'

const { t } = useI18n()
const { getSortItems, getCategoryItems, isCategoryHidden, fetchCategories } =
  useNavigation()
const navStore = useNavigation()
const router = useRouter()
const eventsStore = useEvents()
const authStore = useAuth()

const loading = ref(!navStore.categoriesList.length)

onBeforeMount(async () => {
  await fetchCategories()
  await eventsStore.fetchActiveEvents(!!authStore.auth)
  loading.value = false
})

onServerPrefetch(async () => {
  await fetchCategories()
  await eventsStore.fetchActiveEvents(!!authStore.auth)
  loading.value = false
})

const currentCategorySlug = computed(() => {
  const category = router.currentRoute.value.params.category as string

  if (
    category &&
    Object.keys(navStore.categoriesSlugMap).length &&
    !navStore.categoriesSlugMap[category]
  ) {
    throw useCreateScreenError({
      message: `category ${category} is not found on index`,
    })
  }

  return category
})

const isActiveCategoryHidden = computed(
  () => isCategoryHidden(currentCategorySlug.value).value
)

const sorts = getSortItems('index', [
  // NavigationSortType.Premium,
  NavigationSortType.All,
  // NavigationSortType.Popular,
  // NavigationSortType.New,
])

const categories = getCategoryItems('index')

const topbarMenu: ComputedRef<TopbarMenuInterface[]> = computed(() => [
  {
    title: t('menu.all_news'),
    items: sorts.value,
    style: 'col',
  },
  {
    title: t('common.categories'),
    items: categories.value,
    style: 'row',
  },
])

watch(
  () => !!authStore.auth,
  () => {
    eventsStore.resetActiveEvents()
    eventsStore.fetchActiveEvents(!!authStore.auth)
  }
)
</script>
